import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, delay } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseService } from './base.service';
import { PaginationParams } from '../interfaces/user.interface';
import {
  Authorization,
  AuthorizationRequest,
  AuthorizationResponse,
  GivenAuthorizationResponse,
  AuthorizationRequestResponse,
  AuthorizationApiResponse,
  ApproveRequestDto,
  RejectRequestDto,
  Permission,
} from '../interfaces/authorization.interface';

export interface VerifyAuthorizationDto {
  otp: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService extends BaseService {
  // Données mockées pour les autorisations
  private mockAuthorizations: Authorization[] = [];

  // Données mockées pour les demandes d'autorisation
  private mockAuthorizationRequests: AuthorizationRequest[] = [
  ];

  constructor(http: HttpClient) {
    super(http);
  }

  // Obtenir les autorisations d'un patient
  getAuthorizations(
    uniqueHealthNumber: string,
    params: PaginationParams = {}
  ): Observable<AuthorizationResponse> {
    // Pour le moment, retourner des fausses données
    // TODO: Remplacer par un vrai appel API quand le backend sera prêt
    /*
    const httpParams = this.createParams(params);
    return this.http.get<AuthorizationResponse>(`${this.baseUrl}/authorizations`, {
      headers: this.getHeaders(),
      params: httpParams,
    });
    */

    const page = params.page || 1;
    const limit = params.limit || 10;
    const start = (page - 1) * limit;
    const end = start + limit;

    const paginatedData = this.mockAuthorizations.slice(start, end);

    return of({
      data: paginatedData,
      total: this.mockAuthorizations.length,
      page,
      limit,
    }).pipe(delay(500)); // Simuler un délai réseau
  }

  // Obtenir les autorisations données (où l'utilisateur est l'autoriseur/granter)
  getGivenAuthorizations(
    healthUniqueNumber: string,
    params: { status?: string; page?: number; limit?: number } = {}
  ): Observable<AuthorizationResponse> {
    // Convertir la pagination de 1-based (composant) vers 0-based (API Spring)
    const springParams: any = { ...params };
    if (springParams.page !== undefined && springParams.page !== null) {
      springParams.page = springParams.page - 1; // Convertir de 1-based à 0-based
    }
    // S'assurer que le paramètre status est toujours inclus s'il est défini et en majuscules
    if (springParams.status !== undefined && springParams.status !== null && springParams.status !== '') {
      springParams.status = springParams.status.toUpperCase(); // S'assurer que c'est en majuscules (PENDING ou VALID)
    }
    const httpParams = this.createParams(springParams);
    
    return this.http.get<any>(
      `${this.baseUrl}/authorizations/${healthUniqueNumber}`,
      {
        headers: this.getAuthHeaders(),
        params: httpParams,
      }
    ).pipe(
      // Mapper la réponse API (structure Spring) vers le format interne
      map((response: any) => {
        // Convertir la réponse API vers Authorization[]
        // La réponse peut avoir la structure Spring avec 'content' ou directement un tableau
        const content = response.content || response.data || response || [];
        const authorizations: Authorization[] = content.map((givenAuth: any) => ({
          _id: givenAuth.identifier,
          granteeId: givenAuth.medecin?.medecinId || '',
          granteeName: givenAuth.medecin?.fullName || '',
          granterId: givenAuth.patient?.uniqueHealthNumber || '',
          granterName: givenAuth.patient?.passportNumber || '',
          permissions: this.mapGivenAuthToPermissions(givenAuth),
          scope: 'specific' as const,
          source: {
            type: 'user' as const,
          },
          revocable: true,
          delegationAllowed: false,
          status: this.mapAuthorizationStatus(givenAuth),
          activatedAt: givenAuth.createdAt,
          expiresAt: givenAuth.expiredAt || givenAuth.expiryTime,
          autoGenerated: false,
          immutableWhileAttached: false,
          meta: {
            label: givenAuth.documentInfo?.motif || givenAuth.document?.motif || givenAuth.authorizationType || 'Autorisation',
            notes: givenAuth.message || givenAuth.documentInfo?.motif || '',
            version: 1,
          },
          audit: [],
          createdAt: givenAuth.createdAt,
          updatedAt: givenAuth.updatedAt,
        }));

        return {
          data: authorizations,
          total: response.totalElements || response.total || 0,
          page: (response.number !== undefined ? response.number + 1 : response.page || 1), // Spring utilise 0-based, on convertit en 1-based
          limit: response.size || response.pageable?.pageSize || response.limit || 10,
          rawData: content, // Stocker les données originales pour accéder à l'OTP
        };
      })
    );
  }

  // Helper pour mapper le statut de l'autorisation
  private mapAuthorizationStatus(givenAuth: any): 'active' | 'pending' | 'expired' | 'revoked' | 'rejected' {
    // Si l'API fournit directement un statut, l'utiliser en priorité
    if (givenAuth.status) {
      const status = givenAuth.status.toLowerCase();
      switch (status) {
        case 'pending':
          return 'pending';
        case 'valid':
        case 'active':
        case 'approved':
          return 'active';
        case 'expired':
          return 'expired';
        case 'revoked':
          return 'revoked';
        case 'rejected':
          return 'rejected';
        default:
          // Continuer avec la logique de fallback
          break;
      }
    }

    // Si on a expiredAt, vérifier s'il est expiré
    if (givenAuth.expiredAt) {
      const expiredDate = new Date(givenAuth.expiredAt);
      const currentDate = new Date();
      
      // Si expiredAt est passé → expired
      if (expiredDate < currentDate) {
        return 'expired';
      }
    }

    // Si l'autorisation est autorisée et valide → active
    if (givenAuth.authorized === true && givenAuth.valid === true) {
      return 'active';
    }

    // Si l'autorisation n'est pas encore autorisée → pending
    if (givenAuth.authorized === false) {
      return 'pending';
    }

    // Si valid: false mais authorized: true → peut être révoquée ou rejetée
    // Analyser le message pour déterminer
    const message = givenAuth.message?.toLowerCase() || '';
    if (message.includes('rejet') || message.includes('reject') || message.includes('refus')) {
      return 'rejected';
    }
    if (message.includes('révoq') || message.includes('revok')) {
      return 'revoked';
    }

    // Par défaut, en attente
    return 'pending';
  }

  // Valider une autorisation donnée (pour les autorisations en attente)
  validateGivenAuthorization(
    identifier: string,
    uniqueHealthNumber: string
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/authorizations/verify`,
      { 
        identifier,
        uniqueHealthNumber 
      },
      { headers: this.getAuthHeaders() }
    );
  }

  // Helper pour convertir les données d'autorisation "given" en permissions
  private mapGivenAuthToPermissions(givenAuth: any): Permission[] {
    const documentType = givenAuth.documentInfo?.documentType || givenAuth.document?.documentType || '';
    const accessRight = givenAuth.accessRight || 'READ';
    
    // Mapper le type de document vers un label lisible
    const resourceType = this.mapDocumentTypeToResourceType(documentType);
    
    // Mapper accessRight vers des actions
    const actions = this.mapAccessRightToActions(accessRight);
    
    return [{
      resourceType: resourceType,
      actions: actions,
    }];
  }

  // Helper pour mapper le type de document vers un type de ressource lisible
  private mapDocumentTypeToResourceType(documentType: string): string {
    const documentTypeMap: { [key: string]: string } = {
      'ADMINISTRATIVE_DATA': 'Données administratives',
      'PRESTATION_MEDICAL_DATA': 'Prestations médicales',
      'PRESTATION_FINANCIAL_DATA': 'Prestations financières',
      'COMORBIDITY_DOCUMENT': 'Documents de comorbidité',
      'PRECEDING_DOCUMENT': 'Documents antérieurs',
    };
    
    return documentTypeMap[documentType] || documentType || 'Données';
  }

  // Helper pour mapper accessRight vers des actions
  private mapAccessRightToActions(accessRight: string): string[] {
    const accessRightMap: { [key: string]: string[] } = {
      'READ': ['read'],
      'WRITE': ['read', 'create', 'update'],
      'FULL': ['*'],
      'DELETE': ['read', 'delete'],
    };
    
    return accessRightMap[accessRight?.toUpperCase()] || ['read'];
  }

  // Helper pour convertir accessRight en permissions (legacy)
  private mapAccessRightToPermissions(accessRight: string): Permission[] {
    // Mapper selon le type d'accès
    // Cette logique peut être ajustée selon les valeurs réelles de accessRight
    return [{
      resourceType: '*',
      actions: accessRight === 'full' ? ['*'] : ['read'],
    }];
  }

  // Obtenir les Demandes reçues (où l'utilisateur est le bénéficiaire)
  getReceivedAuthorizations(
    params: { status?: string; page?: number; limit?: number } = {}
  ): Observable<AuthorizationResponse> {
    const httpParams = this.createParams(params);
    
    return this.http.get<AuthorizationApiResponse>(
      `${this.baseUrl}/authorizations/me/received`,
      {
        headers: this.getAuthHeaders(),
        params: httpParams,
      }
    ).pipe(
      // Mapper la réponse API vers le format interne
      map((response: AuthorizationApiResponse) => {
        // Enrichir les données avec granteeName et granterName
        const enrichedData = response.data.map(auth => ({
          ...auth,
          granteeId: auth.grantee?._id || auth.granteeId || '',
          granteeName: auth.grantee 
            ? `${auth.grantee.firstName} ${auth.grantee.lastName}` 
            : auth.granteeName || '',
          granterId: auth.granter?._id || auth.granterId || '',
          granterName: auth.granter 
            ? `${auth.granter.firstName} ${auth.granter.lastName}` 
            : auth.granterName || '',
        }));

        return {
          data: enrichedData,
          total: response.meta.totalItems,
          page: response.meta.currentPage,
          limit: response.meta.pageSize,
        };
      })
    );
  }

  // Obtenir les demandes d'autorisation
  getAuthorizationRequests(
    uniqueHealthNumber: string,
    params: PaginationParams = {}
  ): Observable<AuthorizationRequestResponse> {
    // Pour le moment, retourner des fausses données
    // TODO: Remplacer par un vrai appel API quand le backend sera prêt
    /*
    const httpParams = this.createParams(params);
    return this.http.get<AuthorizationRequestResponse>(
      `${this.baseUrl}/authorization-requests`,
      {
        headers: this.getHeaders(),
        params: httpParams,
      }
    );
    */

    const page = params.page || 1;
    const limit = params.limit || 10;
    const start = (page - 1) * limit;
    const end = start + limit;

    const paginatedData = this.mockAuthorizationRequests.slice(start, end);

    return of({
      data: paginatedData,
      total: this.mockAuthorizationRequests.length,
      page,
      limit,
    }).pipe(delay(500)); // Simuler un délai réseau
  }

  // Vérifier une autorisation avec OTP
  verifyAuthorization(
    uniqueHealthNumber: string,
    verifyData: VerifyAuthorizationDto
  ): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/authorizations/${uniqueHealthNumber}/verify`,
      verifyData,
      { headers: this.getHeaders() }
    );
  }

  // Révoquer une autorisation
  revokeAuthorization(identifier: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/authorizations/${identifier}/revoke`, {
      headers: this.getAuthHeaders(),
    });
  }

  // Approuver une demande d'autorisation
  approveAuthorizationRequest(
    requestId: string,
    approveData: ApproveRequestDto
  ): Observable<any> {
    // TODO: Remplacer par un vrai appel API
    /*
    return this.http.post(
      `${this.baseUrl}/authorization-requests/${requestId}/approve`,
      approveData,
      { headers: this.getHeaders() }
    );
    */

    // Simuler l'approbation
    const index = this.mockAuthorizationRequests.findIndex(
      (req) => req._id === requestId
    );
    if (index !== -1) {
      this.mockAuthorizationRequests[index].status = 'approved';
      this.mockAuthorizationRequests[index].respondedAt = new Date();
      this.mockAuthorizationRequests[index].audit.push({
        event: 'approved',
        at: new Date(),
        note: approveData.notes || 'Approuvée par le patient',
      });

      // Créer une nouvelle autorisation basée sur la demande
      const request = this.mockAuthorizationRequests[index];
      const newAuth: Authorization = {
        _id: `auth_${Date.now()}`,
        granteeId: request.requesterId,
        granteeName: request.requesterName,
        granterId: request.targetId,
        granterName: request.targetName,
        permissions: approveData.approvedPermissions || request.permissions,
        scope: request.scope,
        source: request.source,
        revocable: true,
        delegationAllowed: false,
        status: 'active',
        activatedAt: new Date(),
        expiresAt: approveData.expiresAt || request.expiresAt,
        autoGenerated: false,
        immutableWhileAttached: false,
        meta: {
          label: request.meta?.label || 'Autorisation accordée',
          notes: approveData.notes || request.meta?.notes,
          version: 1,
        },
        audit: [
          {
            event: 'created',
            at: new Date(),
            note: 'Créée suite à approbation de demande',
          },
        ],
      };
      this.mockAuthorizations.unshift(newAuth);
    }

    return of({
      success: true,
      message: 'Demande approuvée avec succès',
    }).pipe(delay(300));
  }

  // Rejeter une demande d'autorisation
  rejectAuthorizationRequest(
    requestId: string,
    rejectData: RejectRequestDto
  ): Observable<any> {
    // TODO: Remplacer par un vrai appel API
    /*
    return this.http.post(
      `${this.baseUrl}/authorization-requests/${requestId}/reject`,
      rejectData,
      { headers: this.getHeaders() }
    );
    */

    // Simuler le rejet
    const index = this.mockAuthorizationRequests.findIndex(
      (req) => req._id === requestId
    );
    if (index !== -1) {
      this.mockAuthorizationRequests[index].status = 'rejected';
      this.mockAuthorizationRequests[index].respondedAt = new Date();
      this.mockAuthorizationRequests[index].audit.push({
        event: 'rejected',
        at: new Date(),
        note: rejectData.reason,
      });
    }

    return of({ success: true, message: 'Demande rejetée' }).pipe(delay(300));
  }

  // Obtenir les utilisateurs accessibles
  getAccessibleUsers(): Observable<any> {
    return this.http.get<any>(
      `${this.baseUrl}/authorizations/me/accessible-users`,
      {
        headers: this.getAuthHeaders(),
      }
    );
  }
}
